<template>
  <div id="orders-dine-in-data-table">
    <b-table
      v-dragscroll
      :items="items"
      :fields="tableFields"
      responsive
      show-empty
      :empty-text="emptyText"
      :busy.sync="loading"
      @sort-changed="handleSortBy"
      class="base-table mt-4"
    >
      <template slot="order_transaction_status" slot-scope="data">
        <div v-html="data.value"></div>
      </template>
      <template slot="payment_status" slot-scope="data">
        <div v-html="data.value"></div>
      </template>
      <template slot="action" slot-scope="data">
        <b-button
          variant="link"
          class="action-button-circle"
          @click="ordersDineInViewOrderDetail(data)"
        >
          <img src="@/assets/images/icons/open-eye.svg" alt="View" />
        </b-button>
      </template>
    </b-table>
  </div>
</template>

<script>
import { computed, defineComponent, watch } from "@vue/composition-api";
import { DateTime } from "luxon";
import _ from "lodash";
import { dragscroll } from "vue-dragscroll";

export default defineComponent({
    directives: { dragscroll },
    setup(props, { root, emit }) {
        const loading = computed(() => root.$store.state.ordersDineIn.loading);
        const emptyText = computed(
            () => (loading.value ? "Loading..." : "No records found.")
        );
        const apiErrors = computed(
            () => root.$store.state.ordersDineIn.errors || {}
        );
        const orderStatusOptions = [
            { text: "New", value: "pending" },
            { text: "Preparing", value: "in_process" },
            { text: "Served", value: "completed" },
            { text: "Cancelled", value: "cancelled" }
        ];
        const paymentStatusOptions = [
            { text: "Processing", value: "pending" },
            { text: "Completed", value: "success" },
            { text: "Failed", value: "failed" },
            { text: "Cancelled", value: "cancelled" }
        ];
        const statusClasses = (status, type) => {
            const spanClasses = [];
            if (
                status === "in_process" ||
                (status === "pending" && type === "payment")
            )
                spanClasses.push("bg-processing", "text-white");
            else if (status === "pending")
                spanClasses.push("bg-info", "text-white");
            else if (status === "completed" || status === "success")
                spanClasses.push("bg-completed", "text-white");
            else spanClasses.push("bg-secondary", "text-white");
            return spanClasses;
        };
        const tableFields = [
            {
                key: "transaction_no",
                label: "Reference Number",
                sortable: true,
                thClass: "text-left text-nowrap",
                tdClass: "text-left"
            },
            {
                key: "customer_name",
                sortable: true,
                thClass: "text-nowrap",
                formatter: (value, key, item) => {
                    if (item.is_guest_customer) return `${value} (Guest)`;
                    return value;
                }
            },
            {
                key: "created_at",
                label: "Date & Time",
                sortable: true,
                thClass: "text-nowrap",
                formatter: value => {
                    return DateTime.fromSQL(value).toFormat(
                        "LL/dd/yyyy hh:mma"
                    );
                }
            },
            {
                key: "brand_name",
                label: "Brand & Branch",
                sortable: true,
                thClass: "text-nowrap",
                formatter: (value, key, item) =>
                    `${value} - ${item.branch_name}`
            },
            {
                key: "voucher_list",
                label: "Promo Code",
                thClass: "text-nowrap",
                formatter: (value, key, item) => {
                    if (
                        _.has(item, "voucher_list") &&
                        value.filter(promo => promo).length
                    )
                        return value
                            .filter(promo => promo)
                            .map(promo => promo.name)
                            .join(", ");
                    else return "N/A";
                }
            },
            {
                key: "amount_to_pay",
                label: "Discounted Amount",
                sortable: true,
                thClass: "text-nowrap",
                formatter: (value, key, item) => {
                    if (_.get(item, "voucher_list.length")) {
                        return `₱ ${Number(
                            parseFloat(value).toFixed(2)
                        ).toLocaleString("en", {
                            minimumFractionDigits: 2
                        })}`;
                    }

                    return "N/A";
                }
            },
            {
                key: "computed_total_amount",
                label: "Amount",
                sortable: true,
                thClass: "text-nowrap",
                formatter: value => {
                    return `₱ ${Number(
                        parseFloat(value).toFixed(2)
                    ).toLocaleString("en", {
                        minimumFractionDigits: 2
                    })}`;
                }
            },
            { key: "payment_method", thClass: "text-nowrap" },
            { 
                key: "points_redeemed",
                sortable: true, 
                thClass: "text-nowrap",
                formatter: (value) => formatThousandsSeparator(value)
            },
            { 
                key: "points_earned", 
                sortable: true, 
                thClass: "text-nowrap",
                formatter: (value) => formatThousandsSeparator(value)
            },
            {
                key: "order_transaction_status",
                label: "Order Status",
                thClass: "text-nowrap",
                formatter: value => {
                    const status = orderStatusOptions.find(
                        status => status.value === value
                    );
                    if (value && status)
                        return `<span class="status-style d-inline-block text-center rounded-pill py-1 px-2 ${statusClasses(
                            value,
                            "order"
                        ).join(" ")}">${status.text}</span>`;
                    else return "N/A";
                }
            },
            {
                key: "payment_status",
                thClass: "text-nowrap",
                formatter: value => {
                    const status = paymentStatusOptions.find(
                        status => status.value === value
                    );
                    if (value && status)
                        return `<span class="status-style d-inline-block text-center rounded-pill py-1 px-2 payment-status ${statusClasses(
                            value,
                            "payment"
                        ).join(" ")}">${status.text}</span>`;
                    else return "N/A";
                }
            },
            "action"
        ];
        const items = computed(
            () => root.$store.state.ordersDineIn.dine_in_orders || []
        );
        const ordersDineInViewOrderDetail = async ({ item }) => {
            await root.$store.dispatch(
                "ordersDineIn/getOrderDineInDetails",
                item.uuid
            );
            if (_.isEmpty(apiErrors.value))
                root.$emit(
                    "bv::show::modal",
                    "orders-dine-in-order-details-modal"
                );
        };
        const handleSortBy = ({ sortBy, sortDesc }) => {
            const computedKey =
                sortBy === "amount_to_pay" ? "computed_total_amount" : sortBy;
            emit("setOrdersDineInTableSort", {
                sort: { by: computedKey, direction: sortDesc ? "asc" : "desc" }
            });
        };
        const formatThousandsSeparator = (value) => value ? new Intl.NumberFormat('en-US').format(value) : value;

        watch(items, () => {
            const table = document.getElementsByClassName("base-table")[0];

            if (table && table.scrollLeft > 0) {
                table.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth"
                });
            }
        });

        return {
            loading,
            emptyText,
            items,
            tableFields,
            ordersDineInViewOrderDetail,
            handleSortBy
        };
    }
});
</script>

<style lang="scss">
#orders-dine-in-data-table {
    .b-table {
        table-layout: fixed;
    }
    th {
        width: 177px;
    }
    th:nth-child(1),
    th:nth-child(2) {
        width: 200px;
    }
    th:nth-child(3) {
        width: 220px;
    }
    th:nth-child(4) {
        width: 302px;
    }
    th:nth-child(5) {
        width: 131px;
    }
    th:nth-child(6) {
        width: 220px;
    }
    .bg-processing {
        background-color: #fdd756;
    }
    .bg-completed {
        background-color: #1e9a5f;
    }
    .status-style {
        width: 90px;
    }
    .action-button {
        top: -2px;
    }
}
</style>
